import {
    formatIncomeSplitPercentages,
    type IncomeSplitConfigLegacy,
    IncomeSplitDetails,
    IncomeSplitRulesList,
    IncomeSplitRuleType,
} from '@fruitful/income-split';
import { AnalyticsUserEvent, type DepositAccount } from '@fruitful/types';
import { useIsFocused } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Redirect, router } from 'expo-router';
import { useSetAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Checkbox,
    FootnoteRegular,
    H2Emphasized,
    Icon,
    InfoIcon,
    LabelRegular,
    ScreenContainer,
    Sheet,
    Spinner,
    SplitIcon,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { captureException, track } from '../../../logger/Logger';
import { SUPPORT_EMAIL } from '../../../settings/types/constants';
import { invalidateIncomeSplitAndMoneyMapQueries } from '../../hooks/income-split/invalidateIncomeSplitAndMoneyMapQueries';
import useSaveIncomeSplit from '../../hooks/income-split/useSaveIncomeSplit';
import useGetExistingPaycheckConfigs from '../../hooks/paycheck-routing/useGetPaycheckConfigs';
import useGetInternalAndExternalDepositAccounts from '../../hooks/useGetInternalAndExternalDepositAccounts';
import { incomeSplitAtomLegacy } from '../../state/PaycheckRoutingAtoms';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import type { IncomeSplitContext } from '../../types/IncomeSplit';
import { PaycheckRoutingStatus } from '../../types/PaycheckRouting';
import { isAccountValidAsIncomeSplitSource } from '../../utils/IncomeSplitUtils';
import {
    FRUITFUL_FDIC_DISCLOSURE,
    INVESTMENT_PRODUCTS_FDIC_DISCLOSURE,
    ONE_TIME_EXTERNAL_TRANSFER_DISCLOSURE_TEXT,
    STOP_PAYMENT_DISCLOSURE_TEXT,
} from '../Disclosures';
import { RegularLine } from './IncomeSplitLines';

interface HeaderProps {
    context: IncomeSplitContext;
}

const Header = ({ context }: HeaderProps) => {
    const headerMessage =
        context === 'goButtonFlow'
            ? 'Review your income rules. You can manage Income Split in your Fruitful Cash settings.'
            : 'Starting with your next deposit, transfers will process from top to bottom subject to available funds.';
    return (
        <BodyMediumRegular color="$gray1000">{headerMessage}</BodyMediumRegular>
    );
};

const InfoSheet = ({
    isOpen,
    onClose,
    executeOnlyOnPaychecks,
}: {
    isOpen: boolean;
    onClose: () => void;
    executeOnlyOnPaychecks: boolean;
}) => {
    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={false}>
            <YStack
                gap={32}
                paddingTop={16}
                maxWidth={Platform.OS === 'web' ? 700 : undefined}
            >
                <YStack gap={8}>
                    <BodyLargeEmphasized>
                        How rules are applied to{' '}
                        {executeOnlyOnPaychecks ? 'paychecks' : 'income'}
                    </BodyLargeEmphasized>
                    <BodyMediumRegular color="$gray1100">
                        These rules will be applied to any{' '}
                        {executeOnlyOnPaychecks ? 'paychecks' : 'income'}{' '}
                        arriving in the specified Fruitful bank accounts.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$gray1100">
                        {executeOnlyOnPaychecks
                            ? 'Paychecks are defined as anything that looks like a direct deposit (we do our best to identify paychecks based on available transaction details)'
                            : 'Income is defined as any deposits made into these accounts, such as paychecks, interest earned, or cash back. Excludes ACH returns and reversals, provisional credits, and transfers initiated from Fruitful.'}
                    </BodyMediumRegular>
                </YStack>

                <Button
                    text="Dismiss"
                    variant={ButtonVariant.PRIMARY}
                    onPress={onClose}
                />
            </YStack>
        </Sheet>
    );
};

const InfoSheetIncome = ({
    isOpen,
    onClose,
    isJointMembership,
}: {
    isOpen: boolean;
    onClose: () => void;
    isJointMembership: boolean;
}) => {
    const infoMessage = isJointMembership
        ? 'The following rules will be applied to all income arriving into any joint Fruitful bank account. You can modify this at any time in your Income Split settings.'
        : 'The following rules will be applied to all income arriving into any Fruitful bank account. You can modify this at any time in your Income Split settings.';
    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={true}>
            <YStack paddingHorizontal={16} maxWidth={500}>
                <View
                    alignSelf="center"
                    justifyContent="center"
                    alignItems="center"
                    width={80}
                    height={80}
                    padding={20}
                    marginVertical={16}
                    rotate="180deg"
                    borderRadius={16}
                    backgroundColor="$gray100"
                >
                    <Icon icon={SplitIcon} color="$black" size={32} />
                </View>

                <YStack alignItems="center" gap={8}>
                    <H2Emphasized>Income Split</H2Emphasized>
                </YStack>

                <YStack gap={16} marginTop={32}>
                    <YStack gap={8}>
                        <BodyLargeRegular>How it works</BodyLargeRegular>
                        <BodyMediumRegular color="$gray1000">
                            {infoMessage}
                        </BodyMediumRegular>
                    </YStack>

                    <YStack gap={8}>
                        <BodyLargeRegular>
                            What&apos;s classified as income?
                        </BodyLargeRegular>
                        <BodyMediumRegular color="$gray1000">
                            Income is defined as any deposits made into these
                            accounts, such as paychecks, interest earned, or
                            cash back. Excludes ACH returns and reversals,
                            provisional credits, and transfers initiated from
                            Fruitful.
                        </BodyMediumRegular>
                    </YStack>
                </YStack>

                <View marginTop={32} width="100%">
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Got it"
                        onPress={onClose}
                    />
                </View>
            </YStack>
        </Sheet>
    );
};

const Rules = ({
    incomeSplit,
    bankAccountDetails,
    externalAccounts,
}: {
    incomeSplit: IncomeSplitConfigLegacy;
    bankAccountDetails: BankAccountDisplayDetail[];
    externalAccounts: DepositAccount[];
}) => {
    const [boxHeights, setBoxHeights] = useState<Record<string, number>>({});

    const handleBoxHeightChange = (ruleId: string, height: number) => {
        setBoxHeights((prev) => {
            if (prev[ruleId] === height) return prev;
            return { ...prev, [ruleId]: height };
        });
    };

    return (
        <XStack>
            <View width={20} marginLeft={16} marginTop={-16}>
                {incomeSplit.rules.every(
                    (rule) => (boxHeights[rule.id] ?? 0) > 0,
                ) && (
                    <RegularLine
                        incomeSplit={incomeSplit}
                        boxHeights={boxHeights}
                        isSorting={false}
                        linesAnimated={false}
                        setLinesAnimated={() => {}}
                        context="review"
                    />
                )}
            </View>
            <IncomeSplitRulesList
                incomeSplit={incomeSplit}
                bankAccountDetails={bankAccountDetails}
                externalAccounts={externalAccounts}
                onBoxHeightChange={handleBoxHeightChange}
            />
        </XStack>
    );
};

export const TransferDisclosure = () => {
    return (
        <XStack
            backgroundColor="$gray200"
            borderRadius={16}
            alignItems="flex-start"
            padding={12}
            gap={12}
        >
            <Icon style={{ marginTop: 2 }} icon={InfoIcon} size={24} />
            <YStack flex={1} gap={8}>
                <FootnoteRegular>
                    {ONE_TIME_EXTERNAL_TRANSFER_DISCLOSURE_TEXT}
                </FootnoteRegular>
                <FootnoteRegular>
                    {STOP_PAYMENT_DISCLOSURE_TEXT}
                </FootnoteRegular>
            </YStack>
        </XStack>
    );
};

export const IncomeSplitAgreement = ({
    id,
    checked,
    setChecked,
}: {
    id: string;
    checked: boolean;
    setChecked: (checked: boolean) => void;
}) => {
    let labelContent: React.ReactNode = <></>;

    if (id === 'combined-accept') {
        labelContent = (
            <>
                I acknowledge I have read and agreed to the{' '}
                <Link
                    href="https://www.fruitful.com/legal/income-split-agreement"
                    target="_blank"
                    onPress={(e) => {
                        e.stopPropagation(); // prevents triggering the parent's onPress
                    }}
                >
                    <BodySmallRegular textDecorationLine="underline">
                        Income Split Terms and Conditions
                    </BodySmallRegular>
                </Link>{' '}
                and authorize Fruitful to initiate the transfers of my money as
                indicated above.
            </>
        );
    }

    return (
        <XStack
            gap={8}
            onPress={() => {
                setChecked(!checked);
            }}
            cursor="pointer"
        >
            <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={(isChecked) => {
                    setChecked(isChecked as boolean);
                }}
            />
            <FootnoteRegular flexShrink={1}>{labelContent}</FootnoteRegular>
        </XStack>
    );
};

export const IncomeSplitReview = ({
    incomeSplit,
    bankAccountDetails,
    context,
    incomeSplitComplete,
    onSubmit,
    onIncomeSplitCreated,
}: {
    incomeSplit: IncomeSplitConfigLegacy | null;
    bankAccountDetails: BankAccountDisplayDetail[];
    context: IncomeSplitContext;
    incomeSplitComplete?: boolean;
    onSubmit?: () => void;
    onIncomeSplitCreated?: () => void;
}) => {
    const setIncomeSplit = useSetAtom(incomeSplitAtomLegacy);

    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
    const isFocused = useIsFocused();
    const didAutoOpenInfoSheet = useRef(false);
    const [infoSheetOpen, setInfoSheetOpen] = useState(false);

    // Open once, on the focused review. Saving refetches the money map and
    // remounts this screen under confirmation; the sheet portals to the app
    // root, so that remount would cover the confirmation screen.
    useEffect(() => {
        if (
            !isFocused ||
            didAutoOpenInfoSheet.current ||
            incomeSplitComplete !== false ||
            context !== 'goButtonFlow'
        ) {
            return;
        }
        didAutoOpenInfoSheet.current = true;
        setInfoSheetOpen(true);
    }, [isFocused, incomeSplitComplete, context]);

    const { mutate: createIncomeSplit } = useSaveIncomeSplit();
    const queryClient = useQueryClient();
    const toast = useToastController();

    const { data: depositAccounts } = useGetInternalAndExternalDepositAccounts(
        {},
    );

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );

    const { paycheckRoutingConfigs: incomeSplitConfigs } =
        useGetExistingPaycheckConfigs();

    const incomeSplitAccounts = new Set(
        incomeSplit?.accounts.map((a) => a.accountId),
    );

    const incomeSplitExists = incomeSplitConfigs?.some((a) =>
        incomeSplitAccounts.has(a.accountId),
    );

    const user = useAuthedUser();
    const isJointMembership = !!user.partnerMember;
    const totalEligibleAccounts = bankAccountDetails.filter((account) =>
        isAccountValidAsIncomeSplitSource(user, account),
    );

    if (!incomeSplit) {
        captureException(
            new Error(
                'Tried to load IncomeSplitReview without an IncomeSplitConfig',
            ),
        );
        toast.show(
            `There was an error while loading your Income Split. Please reach out to support at ${SUPPORT_EMAIL}`,
        );
        return context === 'goButtonFlow' ? (
            <Redirect href="/home/roadmap" />
        ) : (
            <Redirect href="/home/banking" />
        );
    }

    const saveIncomeSplit = () => {
        setIsSavingConfig(true);
        onSubmit?.();

        const { isNew, membershipId, ...rest } = incomeSplit;
        createIncomeSplit(
            {
                body: {
                    ...rest,
                    status: PaycheckRoutingStatus.ACTIVE,
                    rules: incomeSplit?.rules.map(
                        ({ isExternal, accountMetadata, ...rule }) => {
                            // first convert percentage to float between 0-1 if necessary
                            const percentage =
                                rule.type === IncomeSplitRuleType.PERCENTAGE
                                    ? null
                                    : rule.percentage != null
                                      ? rule.percentage / 100
                                      : null;

                            const amount =
                                rule.type ===
                                // for legacy reasons, we need to ensure that for percentage rules (rules without a target balance),
                                // that we copy the percentage value to the amount value (converting the percentage from 0-1 to 1-100)
                                // and null out the percentage field
                                IncomeSplitRuleType.PERCENTAGE
                                    ? (rule.percentage ?? 100)
                                    : rule.amount;

                            return {
                                ...rule,
                                amount,
                                percentage,
                            };
                        },
                    ),
                },
            },
            {
                onSuccess: (result) => {
                    setIsSavingConfig(false);
                    setIncomeSplit(
                        formatIncomeSplitPercentages({
                            ...result,
                            membershipId,
                        }),
                    );
                    void invalidateIncomeSplitAndMoneyMapQueries(queryClient);
                    onIncomeSplitCreated?.();
                    router.navigate(
                        `/income-split/confirmation?isEdit=${incomeSplitExists}&context=${context}`,
                    );
                },
                onError: (error) => {
                    setIsSavingConfig(false);
                    toast.show('Error saving Income Split rules');
                    captureException(error);
                },
            },
        );
    };

    return (
        <>
            <ScreenContainer
                title={
                    context === 'incomeSplit'
                        ? 'Review Income Split'
                        : undefined
                }
                hasBackButton={context === 'incomeSplit'}
                onBack={
                    context === 'incomeSplit'
                        ? () => {
                              router.back();
                          }
                        : undefined
                }
                // In the Go flow this is nested under GoWorkflowHeader, whose
                // SafeAreaView already reserves the top inset — without this,
                // ScreenContainer reserves it again since it renders no header
                // of its own here (title/hasBackButton unset), doubling the
                // gap above the content.
                useSafeAreaInsetTop={context !== 'goButtonFlow'}
            >
                <YStack flex={1} gap={24} justifyContent="space-between">
                    <YStack gap={16}>
                        <Header context={context} />
                        <IncomeSplitDetails
                            incomeSplit={incomeSplit}
                            totalEligibleAccounts={totalEligibleAccounts}
                            icon={InfoIcon}
                            onIconPress={() => {
                                setInfoSheetOpen(true);
                            }}
                        />
                        <Rules
                            incomeSplit={incomeSplit}
                            externalAccounts={externalAccounts}
                            bankAccountDetails={bankAccountDetails}
                        />
                        <TransferDisclosure />
                        <IncomeSplitAgreement
                            id="combined-accept"
                            checked={hasAcceptedAgreement}
                            setChecked={(checked) => {
                                setHasAcceptedAgreement(checked);
                            }}
                        />
                    </YStack>

                    <YStack>
                        <Button
                            text={
                                isSavingConfig
                                    ? ''
                                    : 'Confirm and turn on Income Split'
                            }
                            icon={isSavingConfig ? <Spinner /> : null}
                            variant={ButtonVariant.PRIMARY}
                            onPress={saveIncomeSplit}
                            disabled={!hasAcceptedAgreement}
                        />
                        <LabelRegular color="$gray900" marginTop={24}>
                            {FRUITFUL_FDIC_DISCLOSURE}
                            {'\n\n'}
                            {INVESTMENT_PRODUCTS_FDIC_DISCLOSURE}
                        </LabelRegular>
                    </YStack>
                </YStack>
            </ScreenContainer>
            {isFocused &&
                (incomeSplit.onlyExecuteOnPaychecks ? (
                    <InfoSheet
                        isOpen={infoSheetOpen}
                        onClose={() => {
                            track(
                                AnalyticsUserEvent.INCOME_SPLIT_INFO_SHEET_DISMISSED,
                            );
                            setInfoSheetOpen(false);
                        }}
                        executeOnlyOnPaychecks={
                            incomeSplit.onlyExecuteOnPaychecks
                        }
                    />
                ) : (
                    <InfoSheetIncome
                        isOpen={infoSheetOpen}
                        onClose={() => {
                            track(
                                AnalyticsUserEvent.INCOME_SPLIT_INFO_SHEET_DISMISSED,
                            );
                            setInfoSheetOpen(false);
                        }}
                        isJointMembership={isJointMembership}
                    />
                ))}
        </>
    );
};
